import React from 'react';
import { useTranslation } from 'react-i18next';

import { sqlDateToLabel, formatAmountValue } from 'helpers/helpers';
import MaterialTable from 'components/Table/Table';
import Info from 'components/Tooltip/Info';
import { MovementsStore, Movement } from 'stores/account/movementsStore';
import { AccountMode, Currency } from 'enums';
import useStyles from './movementsStyles';

//import { movements } from 'stores/account/mock.js';

interface Props {
  store: MovementsStore;
  mode: AccountMode;
}

const PAGE_SIZE_KEY = 'movementsPageSize';

const MovementsTable: React.FC<Props> = ({ store, mode }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const pageSizeStorageValue = localStorage.getItem(PAGE_SIZE_KEY);
  const pageSize = pageSizeStorageValue ? parseInt(pageSizeStorageValue) : 20;

  const onChangeRowsPerPage = (pageSize: number) => {
    localStorage.setItem(PAGE_SIZE_KEY, JSON.stringify(pageSize));
  };

  const typeClassNameStyle = (type: string): string | undefined => {
    switch (type) {
      case 'FAILED_DEPOSIT':
        return classes.typeFailedDeposit;
      default:
        return undefined;
    }
  };

  const tableRef = React.createRef<any>();

  const withWrapper = (content: any, wrap: boolean, tooltip?: string) => {
    return wrap ? (
      <div className={classes.wrapper}>
        {content}
        {tooltip ? <Info content={t(tooltip)} table /> : ''}
      </div>
    ) : (
      content
    );
  };

  return store.data!.movements ? (
    <MaterialTable
      tableRef={tableRef}
      className={classes.table}
      options={{
        header: true,
        sorting: true,
        draggable: false,
        search: false,
        toolbar: false,
        paging: store.data!.movements?.length > pageSize,
        pageSize,
        rowStyle: (rowData, index, level) => {
          //return { color: colors[rowData.assetCategory] };
          return {
            color: rowData.grouped ? undefined : 'rgba(0,0,0,0)',
            backgroundColor: rowData.grouped ? undefined : 'rgba(0,0,0,0)',
            cursor: rowData.grouped ? 'pointer' : undefined,
          };
        },
      }}
      quickfix
      onRowsPerPageChange={onChangeRowsPerPage}
      data={store.data!.movements}
      //data={movements}
      columns={[
        {
          title: t('general_portfolio'),
          field: 'portfolio',
          render: ({ portfolio, grouped }) => withWrapper(t(`accountDetail_movements_portfolio_${portfolio}`), grouped),
        },
        {
          title: t('general_date'),
          field: 'date',
          defaultSort: 'desc',
          render: ({ date, grouped }) => withWrapper(t(`${sqlDateToLabel(date, 'dd. MM. yyyy')}`), grouped),
        },
        {
          title: t('general_type'),
          field: 'type',
          render: ({ type, isin, grouped }) => {
            return withWrapper(
              <>
                <span
                  className={typeClassNameStyle(type)}
                  dangerouslySetInnerHTML={{
                    __html: t(
                      `accountDetail_movements_type_${type}${
                        mode === AccountMode.LTIP && ['CASH_DEPOSIT', 'EMPLOYER_DEPOSIT'].includes(type) ? '_LTIP' : ''
                      }`,
                      {
                        interpolation: { escapeValue: false },
                      },
                    ),
                  }}
                />
                {isin ? ` - ${isin}` : ''}
              </>,
              grouped,
            );
          },
        },
        {
          title: t('general_amount'),
          field: 'amount',
          cellStyle: {
            textAlign: 'right',
            paddingRight: '36px',
            whiteSpace: 'nowrap',
            justifyContent: 'end',
            //cursor: 'pointer'
          },
          headerStyle: {
            textAlign: 'right',
          },
          render: ({ amount, grouped }) =>
            withWrapper(
              amount ? formatAmountValue(amount.value, amount.currency) : '',
              grouped,
              grouped ? 'accountDetail_movements_grouped_tooltip' : undefined,
            ),
        },
      ]}
      detailPanel={[
        {
          disabled: true,
          render: ({ rowData }) =>
            rowData.grouped ? (
              <table
                style={{ background: '#f5f5f5', borderCollapse: 'collapse', tableLayout: 'auto', width: '100%' }}
                className="detailTable"
              >
                <thead>
                  <tr>
                    <th style={{ padding: 0 }} />
                    <th style={{ width: '25.8%', padding: '8px 16px' }} />
                    <th style={{ width: '25%', padding: '8px 16px' }} />
                    <th style={{ width: '25%', padding: '8px 16px 8px 40px' }}>
                      {t('accountDetail_movements_instrument')}
                    </th>
                    <th style={{ width: '25%', padding: '8px 36px 8px 16px' }}>
                      <span />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rowData.items.map((item: Movement) => (
                    <tr key={item.id}>
                      <td style={{ padding: 0 }}>
                        <div style={{ width: '42px' }} />
                      </td>
                      <td style={{ padding: '8px 16px' }} />
                      <td style={{ padding: '8px 16px' }} />
                      <td style={{ padding: '8px 16px 8px 40px' }}>
                        {t(`accountDetail_movements_transfer_${item.type}`)}
                        {item.isin && (
                          <>
                            <br />
                            {item.isin}
                          </>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px 36px 8px 16px' }}>
                        {formatAmountValue(item.amount.value, item.amount.currency as Currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null,
        },
      ]}
      onRowClick={(event, rowData, togglePanel) => {
        if (tableRef?.current) {
          tableRef.current.state.data = tableRef.current.state.data!.map((data: any) => {
            if (rowData.id !== data.id) {
              data.tableData.showDetailPanel = false;
            }
            return data;
          });
        }
        if (rowData.grouped) {
          togglePanel!();
        }
      }}
    />
  ) : null;
};

export default MovementsTable;
